/* eslint-disable */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import * as url from "helper/UrlHelper";

const getStatusBadge = (completedpercent) => {
  switch (completedpercent) {
    case "0": return { color: "bg-secondary", label: "Pending" };
    case "1": return { color: "bg-primary",   label: "In Progress" };
    case "3": return { color: "bg-success",   label: "Completed" };
    case "4": return { color: "bg-danger",    label: "Failed" };
    default:  return { color: "bg-secondary", label: "Pending" };
  }
};

// Fixed callback token – must match N8N_CALLBACK_TOKEN on the server
const N8N_CALLBACK_TOKEN = "lobees-n8n-callback-2025";

const buildPayloadPreview = (automationConfig, isReactive, apiBaseUrl) => {
  const extra =
    automationConfig?.payload_template &&
    typeof automationConfig.payload_template === "object" &&
    Object.keys(automationConfig.payload_template).length > 0
      ? automationConfig.payload_template : {};
  if (isReactive) {
    return {
      callbackUrl: `${apiBaseUrl}/automationtaskschedule/webhook/n8n/callback`,
      taskid: "{{taskid}}", email: "{{email}}", name: "{{name}}", phone: "{{phone}}",
      token: N8N_CALLBACK_TOKEN,
      webhook_name: automationConfig?.webhook_name || "",
      ...extra,
    };
  }
  return {
    callbackUrl: `${apiBaseUrl}/automationtaskschedule/webhook/n8n/callback`,
    taskid: "{{taskid}}", email: "{{email}}", name: "{{name}}", phone: "{{phone}}",
    token: N8N_CALLBACK_TOKEN,
    ...extra,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// AutomationChildTaskDetailPopup
// Receives the full child task object as a prop (no extra API call needed)
// ─────────────────────────────────────────────────────────────────────────────
const AutomationChildTaskDetailPopup = ({ task, isOpen, onClose }) => {
  const { t } = useTranslation();

  const [webhookUrl, setWebhookUrl] = useState("");
  const [isEditingWebhook, setIsEditingWebhook] = useState(false);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null); // { success, message }

  // Sync local webhook URL when task changes
  React.useEffect(() => {
    if (task?.automation?.webhook_url !== undefined) {
      setWebhookUrl(task.automation.webhook_url || "");
    }
    setIsEditingWebhook(false);
    setTriggerResult(null);
  }, [task?._id]);

  if (!isOpen || !task) return null;

  const isReactive = task.executiontype === "reactive";
  const automationConfig = task.automation || null;
  const statusBadge = getStatusBadge(task.completedpercent);
  const proactiveUrl = `${url.API_BASE_URL}/automationtaskschedule/proactive?email=${task.moderator?.email || "{user_email}"}`;

  const handleSaveWebhookUrl = async () => {
    if (!webhookUrl.trim()) return;
    setWebhookSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${url.API_BASE_URL}/automationtaskschedule/childtask/${task._id}/webhook?token=${token}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ webhook_url: webhookUrl.trim() }),
      });
      const data = await res.json();
      if (data.status) {
        setIsEditingWebhook(false);
      } else {
        alert(data.message || "Failed to update webhook URL");
      }
    } catch {
      alert("Error saving webhook URL");
    } finally {
      setWebhookSaving(false);
    }
  };

  const handleTrigger = async () => {
    setTriggering(true);
    setTriggerResult(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${url.API_BASE_URL}/automationtaskschedule/childtask/${task._id}/trigger?token=${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      setTriggerResult({ success: data.status, message: data.message });
    } catch {
      setTriggerResult({ success: false, message: "Network error while triggering" });
    } finally {
      setTriggering(false);
    }
  };

  const previewJson = (() => {
    const preview = buildPayloadPreview(automationConfig, isReactive, url.API_BASE_URL);
    return preview ? JSON.stringify(preview, null, 2) : null;
  })();

  return (
    <>
      <div
        className="offcanvas offcanvas-end bg-white border-0 shadow-lg show"
        style={{ visibility: "visible", width: "80vw", maxWidth: "960px" }}
        tabIndex="-1"
        aria-labelledby="offcanvasChildTaskDetailLabel"
      >
        {/* Header */}
        <div className="offcanvas-header bg-primary text-white p-4 border-bottom">
          <div className="d-flex align-items-center gap-3">
            <div className="p-2 rounded-circle d-flex align-items-center justify-content-center"
              style={{ background: "rgba(255,255,255,0.15)" }}>
              <span className="material-symbols-outlined text-white" style={{ fontSize: "22px" }}>
                subdirectory_arrow_right
              </span>
            </div>
            <div>
              <h5 className="offcanvas-title fw-bold text-white mb-1" id="offcanvasChildTaskDetailLabel">
                {task.title}
              </h5>
              <div className="d-flex align-items-center gap-2">
                <span className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}>
                  {isReactive ? t("Reactive") : t("Proactive")}
                </span>
                <span className={`badge ${statusBadge.color}`} style={{ fontSize: "11px" }}>
                  {t(statusBadge.label)}
                </span>
                {task.customid && (
                  <small className="text-white opacity-75">#{task.customid}</small>
                )}
              </div>
            </div>
          </div>
          <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close" />
        </div>

        {/* Body */}
        <div className="offcanvas-body p-4" style={{ overflowY: "auto" }}>

          {/* ── 1. Task Info + Parent ── */}
          <div className="row g-4 mb-4">
            {/* Task Info */}
            <div className="col-xl-6">
              <div className="h-100 p-4 rounded-3 border border-primary border-opacity-25 shadow-sm bg-light">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: "20px" }}>task_alt</span>
                  <h6 className="mb-0 fw-bold text-dark">{t("Task Info")}</h6>
                </div>
                <h5 className="fw-bold text-dark mb-2">{task.title}</h5>
                {task.description && (
                  <p className="text-muted mb-3" style={{ fontSize: "14px", lineHeight: "1.6" }}>{task.description}</p>
                )}
                <div className="row g-2">
                  {task.customid && (
                    <div className="col-6">
                      <small className="text-muted d-flex align-items-center gap-1 mb-1">
                        <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>tag</span>
                        {t("Custom ID")}
                      </small>
                      <span className="badge bg-light text-dark border fw-semibold">{task.customid}</span>
                    </div>
                  )}
                  <div className="col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>flag</span>
                      {t("Status")}
                    </small>
                    <span className={`badge ${statusBadge.color}`}>{t(statusBadge.label)}</span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>calendar_today</span>
                      {t("Schedule Date")}
                    </small>
                    <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                      {task.startdate || "—"}
                    </span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>schedule</span>
                      {t("Schedule Time")}
                    </small>
                    <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                      {task.starttime || "—"}
                    </span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>bolt</span>
                      {t("Execution Type")}
                    </small>
                    <span className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}>
                      {isReactive ? t("Reactive") : t("Proactive")}
                    </span>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-flex align-items-center gap-1 mb-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>event</span>
                      {t("Created")}
                    </small>
                    <span className="fw-semibold text-dark" style={{ fontSize: "13px" }}>
                      {new Date(task.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Lead + Parent Task */}
            <div className="col-xl-6 d-flex flex-column gap-3">
              {/* Lead */}
              {task.leadid && (
                <div className="p-4 rounded-3 border border-success border-opacity-25 shadow-sm bg-light">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="material-symbols-outlined text-success" style={{ fontSize: "20px" }}>person</span>
                    <h6 className="mb-0 fw-bold text-dark">{t("Lead")}</h6>
                  </div>
                  <div className="fw-semibold text-dark mb-1">
                    {task.leadid.name} {task.leadid.surname}
                  </div>
                  {task.leadid.email && (
                    <div className="text-muted d-flex align-items-center gap-1 mb-1" style={{ fontSize: "13px" }}>
                      <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>mail</span>
                      {task.leadid.email}
                    </div>
                  )}
                  {task.leadid.phone && (
                    <div className="text-muted d-flex align-items-center gap-1 mb-1" style={{ fontSize: "13px" }}>
                      <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>call</span>
                      {task.leadid.phone}
                    </div>
                  )}
                  {task.leadid.company && (
                    <div className="text-secondary d-flex align-items-center gap-1" style={{ fontSize: "13px" }}>
                      <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>business</span>
                      {task.leadid.company}
                    </div>
                  )}
                </div>
              )}

              {/* Parent Task */}
              {task.previoustask && (
                <div className="p-4 rounded-3 border border-warning border-opacity-25 shadow-sm bg-light">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="material-symbols-outlined text-warning" style={{ fontSize: "20px" }}>account_tree</span>
                    <h6 className="mb-0 fw-bold text-dark">{t("Parent Task")}</h6>
                    <span className="badge bg-warning text-dark ms-auto" style={{ fontSize: "10px" }}>{t("Parent")}</span>
                  </div>
                  <span className="fw-semibold text-dark" style={{ fontSize: "14px" }}>
                    {task.previoustask.title || task.previoustask}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── 2. Callback Config (fixed token – same for all tasks) ── */}
          <div className="border rounded-3 overflow-hidden shadow-sm mb-4">
            <div className="px-4 py-3 border-bottom d-flex align-items-center gap-2"
              style={{ background: "rgba(108,117,125,0.07)" }}>
              <span className="material-symbols-outlined text-secondary" style={{ fontSize: "20px" }}>lock</span>
              <h6 className="mb-0 fw-semibold text-dark">{t("n8n Callback Config")}</h6>
              <small className="text-muted">— {t("n8n must POST this to update task status")}</small>
              <span className="badge bg-secondary ms-auto" style={{ fontSize: "10px" }}>POST</span>
            </div>
            <div className="p-4">
              <div className="row g-3">
                <div className="col-xl-6">
                  <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                    <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>link</span>
                    {t("Callback URL")}
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <code className="flex-grow-1 p-2 rounded-2 border"
                      style={{ background: "#1e1e2e", color: "#cdd6f4", fontSize: "12px", wordBreak: "break-all", display: "block", lineHeight: "1.6" }}>
                      {`${url.API_BASE_URL}/automationtaskschedule/webhook/n8n/callback`}
                    </code>
                    <button type="button" className="btn btn-sm btn-outline-secondary flex-shrink-0"
                      onClick={() => navigator.clipboard?.writeText(`${url.API_BASE_URL}/automationtaskschedule/webhook/n8n/callback`)}>
                      <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>content_copy</span>
                    </button>
                  </div>
                </div>
                <div className="col-xl-6">
                  <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                    <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>key</span>
                    {t("Callback Token")}
                    <span className="badge bg-info text-dark ms-1" style={{ fontSize: "9px" }}>{t("fixed")}</span>
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <input type="text" className="form-control form-control-sm font-monospace bg-light"
                      value={N8N_CALLBACK_TOKEN} readOnly style={{ fontSize: "12px", color: "#555" }} />
                    <button type="button" className="btn btn-sm btn-outline-secondary flex-shrink-0"
                      onClick={() => navigator.clipboard?.writeText(N8N_CALLBACK_TOKEN)}>
                      <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>content_copy</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="mt-3 p-3 rounded-2 border" style={{ background: "#f8f9fa" }}>
                <small className="fw-semibold text-dark d-block mb-1">{t("Expected JSON body from n8n:")}</small>
                <pre className="mb-0 font-monospace" style={{ fontSize: "11.5px", color: "#444", lineHeight: "1.7" }}>
{`{
  "token":    "${N8N_CALLBACK_TOKEN}",
  "taskid":   "{{taskid}}",
  "status":   "3",        // 0=Pending 1=In Progress 3=Completed 4=Failed
  "progress": 100         // 0–100
}`}
                </pre>
              </div>
            </div>
          </div>

          {/* ── 3. Webhook / Payload Config ── */}
          <div className="border rounded-3 overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-bottom d-flex align-items-center gap-2"
              style={{ background: isReactive ? "rgba(13,202,240,0.08)" : "rgba(255,193,7,0.08)" }}>
              <span className="material-symbols-outlined text-secondary" style={{ fontSize: "20px" }}>settings_ethernet</span>
              <h6 className="mb-0 fw-semibold text-dark">
                {isReactive ? t("Webhook Config") : t("Payload Config")}
              </h6>
              <small className="text-muted">
                {isReactive ? `— ${t("payload our server sends to n8n")}` : `— ${t("data our API returns to n8n's CRON")}`}
              </small>
              <span className={`badge ms-auto ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`} style={{ fontSize: "10px" }}>
                {isReactive ? "POST" : "GET"}
              </span>
            </div>

            <div className="row g-0">
              {/* Left: config fields */}
              <div className="col-xl-6 p-4 border-end">
                {isReactive && automationConfig ? (
                  <>
                    <div className="mb-3">
                      <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                        <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>link</span>
                        {t("Webhook URL")}
                      </label>
                      <div className="d-flex gap-2">
                        <input
                          type="text"
                          className="form-control form-control-sm bg-light"
                          value={webhookUrl}
                          readOnly={!isEditingWebhook}
                          onChange={(e) => setWebhookUrl(e.target.value)}
                          style={{ fontSize: "12px", color: "#555" }}
                        />
                        {!isEditingWebhook ? (
                          <button type="button" className="btn btn-sm btn-outline-secondary flex-shrink-0"
                            title={t("Edit")} onClick={() => setIsEditingWebhook(true)}>
                            <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>edit</span>
                          </button>
                        ) : (
                          <>
                            <button type="button" className="btn btn-sm btn-primary flex-shrink-0"
                              disabled={webhookSaving} onClick={handleSaveWebhookUrl}>
                              {webhookSaving
                                ? <span className="spinner-border spinner-border-sm" />
                                : <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>check</span>}
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-secondary flex-shrink-0"
                              onClick={() => { setWebhookUrl(automationConfig.webhook_url || ""); setIsEditingWebhook(false); }}>
                              <span className="material-symbols-outlined" style={{ fontSize: "16px" }}>close</span>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {automationConfig.webhook_name && (
                      <div className="mb-3">
                        <label className="form-label fs-sm fw-semibold text-dark mb-1 d-flex align-items-center gap-1">
                          <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>label</span>
                          {t("Webhook Name")}
                        </label>
                        <input type="text" className="form-control form-control-sm bg-light"
                          value={automationConfig.webhook_name} readOnly style={{ fontSize: "12px", color: "#555" }} />
                      </div>
                    )}

                    {/* Manual Trigger */}
                    <div className="mt-4 pt-3 border-top">
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-success d-flex align-items-center gap-2"
                          disabled={triggering || !webhookUrl}
                          onClick={handleTrigger}
                        >
                          {triggering
                            ? <><span className="spinner-border spinner-border-sm" /> {t("Triggering…")}</>
                            : <><span className="material-symbols-outlined" style={{ fontSize: "16px" }}>play_circle</span> {t("Trigger Webhook")}</>}
                        </button>
                        {triggerResult && (
                          <small className={`d-flex align-items-center gap-1 ${triggerResult.success ? "text-success" : "text-danger"}`}>
                            <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>
                              {triggerResult.success ? "check_circle" : "error"}
                            </span>
                            {triggerResult.message}
                          </small>
                        )}
                      </div>
                    </div>
                  </>
                ) : !isReactive ? (
                  <>
                    <div className="p-3 rounded-2 border mb-3" style={{ background: "#fff8e1" }}>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="material-symbols-outlined text-warning" style={{ fontSize: "18px" }}>api</span>
                        <p className="mb-0 fw-semibold text-dark" style={{ fontSize: "13px" }}>
                          {t("n8n CRON polls this endpoint to get pending tasks:")}
                        </p>
                        <span className="badge bg-warning text-dark ms-auto" style={{ fontSize: "10px" }}>GET</span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <code className="flex-grow-1 p-2 rounded-2 border"
                          style={{ background: "#1e1e2e", color: "#cdd6f4", fontSize: "12px", wordBreak: "break-all", display: "block", lineHeight: "1.8" }}>
                          {proactiveUrl}
                        </code>
                        <button type="button" className="btn btn-sm btn-outline-secondary flex-shrink-0"
                          title={t("Copy URL")} onClick={() => navigator.clipboard?.writeText(proactiveUrl)}>
                          <span className="material-symbols-outlined" style={{ fontSize: "18px" }}>content_copy</span>
                        </button>
                      </div>
                    </div>
                    {/* Manual Trigger for proactive */}
                    <div className="mt-2">
                      <div className="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-success d-flex align-items-center gap-2"
                          disabled={triggering}
                          onClick={handleTrigger}
                        >
                          {triggering
                            ? <><span className="spinner-border spinner-border-sm" /> {t("Triggering…")}</>
                            : <><span className="material-symbols-outlined" style={{ fontSize: "16px" }}>play_circle</span> {t("Trigger Workflow")}</>}
                        </button>
                        {triggerResult && (
                          <small className={`d-flex align-items-center gap-1 ${triggerResult.success ? "text-success" : "text-danger"}`}>
                            <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>
                              {triggerResult.success ? "check_circle" : "error"}
                            </span>
                            {triggerResult.message}
                          </small>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-muted fst-italic" style={{ fontSize: "13px" }}>{t("No automation config found.")}</p>
                )}

                {/* n8n Execution ID */}
                {automationConfig?.n8n_execution_id && (
                  <div className="mt-3">
                    <label className="form-label fs-sm fw-semibold text-dark mb-1">{t("n8n Execution ID")}</label>
                    <code className="d-block p-2 bg-light rounded border" style={{ fontSize: "11px", color: "#555" }}>
                      {automationConfig.n8n_execution_id}
                    </code>
                  </div>
                )}

                {/* Extra Payload */}
                {automationConfig?.payload_template &&
                  typeof automationConfig.payload_template === "object" &&
                  Object.keys(automationConfig.payload_template).length > 0 && (
                    <div className="mt-3">
                      <label className="form-label fs-sm fw-semibold text-dark mb-1">
                        {isReactive ? t("Extra Payload Fields") : t("Payload")}
                      </label>
                      <pre className="rounded-2 p-2 mb-0 bg-light font-monospace"
                        style={{ fontSize: "11px", color: "#555", maxHeight: "120px", overflowY: "auto" }}>
                        {JSON.stringify(automationConfig.payload_template, null, 2)}
                      </pre>
                    </div>
                  )}
              </div>

              {/* Right: payload preview */}
              <div className="col-xl-6 p-4 d-flex flex-column" style={{ background: "#fafafa" }}>
                <label className="form-label fs-sm fw-semibold d-flex align-items-center gap-2 mb-2">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: "16px" }}>preview</span>
                  {t("Payload Preview")}
                  <span className={`badge ms-1 ${isReactive ? "bg-success" : "bg-primary"}`} style={{ fontSize: "10px" }}>
                    {isReactive ? "POST" : "GET"}
                  </span>
                </label>
                {previewJson ? (
                  <pre className="rounded-3 p-3 mb-2 flex-grow-1"
                    style={{ background: "#1e1e2e", color: "#cdd6f4", fontSize: "11.5px", lineHeight: "1.6", maxHeight: "300px", overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                    {previewJson}
                  </pre>
                ) : (
                  <div className="text-center py-3 text-muted flex-grow-1 d-flex align-items-center justify-content-center">
                    <small>{t("No payload configured")}</small>
                  </div>
                )}
                <small className="text-muted d-flex align-items-center gap-1 mt-2">
                  <span className="material-symbols-outlined" style={{ fontSize: "13px" }}>info</span>
                  <code>{"{{taskid}}"}</code>{", "}
                  <code>{"{{email}}"}</code>{", "}
                  <code>{"{{name}}"}</code>{" "}
                  {t("are replaced with real lead data at send time.")}
                </small>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Backdrop */}
      <div className="offcanvas-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default AutomationChildTaskDetailPopup;
